import { attachFileToTask } from '@/lib/api/generated/api';
import { asAttachment, refusalFor } from '../attachmentLimits';

/** How many attachments upload at once: enough to be quick, never all together. */
export const ATTACHMENT_UPLOAD_CONCURRENCY = 3;

export type AttachmentStatus =
  | { kind: 'refused'; reason: string }
  | { kind: 'waiting' }
  | { kind: 'uploading' }
  | { kind: 'done'; storedName: string }
  | { kind: 'failed'; reason: string };

export interface StagedAttachment {
  id: string;
  file: File;
  /** Replace an attachment of the same name rather than keeping both. */
  replace: boolean;
  status: AttachmentStatus;
}

/** Where a queue's files go, once there is a task to send them to. */
export interface UploadTarget {
  workspaceId: string;
  taskId: string;
  /** Called after each stored file, so the task's attachment list can refresh. */
  onUploaded?: () => void;
}

export interface UploadQueue {
  target: UploadTarget | null;
  items: StagedAttachment[];
}

export interface UploadQueuesSlice {
  /** Keyed by a draft key, or `task:<id>` for a task's drawer. */
  queues: Record<string, UploadQueue>;
}

type Get = () => UploadQueuesSlice;
type Set = (next: Partial<UploadQueuesSlice>) => void;

const EMPTY: UploadQueue = { target: null, items: [] };
let nextId = 0;

/**
 * Files on their way to a task's attachments (spec 120 FR-001 – FR-005), kept
 * outside any component (spec 121 FR-005).
 *
 * Files can be added before the task exists — a draft stages them — and
 * nothing is sent until `startUploads` says where they go. From then on at most
 * `ATTACHMENT_UPLOAD_CONCURRENCY` per queue are in flight; each file keeps its
 * own outcome, and a failed one is retried alone. Living in the store rather
 * than the dialog is what lets a minimized dialog, or a page left behind, keep
 * uploading.
 *
 * Status only, never a byte count: the product does not draw progress bars
 * (brand rule FD-16).
 *
 * The double-send guard: the store is read and marked synchronously, so a
 * second pump can never pick a file the first already took.
 */
export function createUploadActions(get: Get, set: Set) {
  const inFlight = new Map<string, number>();

  const queueOf = (queueId: string) => get().queues[queueId] ?? EMPTY;

  const write = (queueId: string, queue: UploadQueue) => {
    set({ queues: { ...get().queues, [queueId]: queue } });
  };

  const mark = (queueId: string, id: string, status: AttachmentStatus) => {
    const queue = get().queues[queueId];
    // The queue was discarded or cleared while the file was on its way.
    if (!queue) return;
    write(queueId, {
      ...queue,
      items: queue.items.map((item) =>
        item.id === id ? { ...item, status } : item,
      ),
    });
  };

  const pump = (queueId: string) => {
    const { target } = queueOf(queueId);
    if (target === null) return;
    while ((inFlight.get(queueId) ?? 0) < ATTACHMENT_UPLOAD_CONCURRENCY) {
      const next = queueOf(queueId).items.find(
        (item) => item.status.kind === 'waiting',
      );
      if (!next) return;
      inFlight.set(queueId, (inFlight.get(queueId) ?? 0) + 1);
      mark(queueId, next.id, { kind: 'uploading' });
      void upload(target, next)
        .then((status) => {
          mark(queueId, next.id, status);
          if (status.kind === 'done') target.onUploaded?.();
        })
        .finally(() => {
          inFlight.set(queueId, (inFlight.get(queueId) ?? 1) - 1);
          pump(queueId);
        });
    }
  };

  return {
    addFiles: (
      queueId: string,
      files: readonly File[],
      { replace = false }: { replace?: boolean } = {},
    ) => {
      const added = files.map((original): StagedAttachment => {
        const file = asAttachment(original);
        const reason = refusalFor(file);
        nextId += 1;
        return {
          id: `attachment-${String(nextId)}`,
          file,
          replace,
          status: reason ? { kind: 'refused', reason } : { kind: 'waiting' },
        };
      });
      const queue = queueOf(queueId);
      write(queueId, { ...queue, items: [...queue.items, ...added] });
      pump(queueId);
    },

    removeFile: (queueId: string, id: string) => {
      const queue = queueOf(queueId);
      write(queueId, {
        ...queue,
        items: queue.items.filter((item) => item.id !== id),
      });
    },

    retryUpload: (queueId: string, id: string) => {
      const item = queueOf(queueId).items.find(
        (candidate) => candidate.id === id,
      );
      if (item?.status.kind !== 'failed') return;
      mark(queueId, id, { kind: 'waiting' });
      pump(queueId);
    },

    startUploads: (queueId: string, target: UploadTarget) => {
      write(queueId, { ...queueOf(queueId), target });
      pump(queueId);
    },
  };
}

/** Files still to be sent or being sent: what a chip counts and a reload would lose. */
export function isPending(item: StagedAttachment) {
  return item.status.kind === 'waiting' || item.status.kind === 'uploading';
}

/**
 * What a created task's chip says about its files (FR-005): "Uploading n/m"
 * while any are on their way — a count, never a bar (brand rule FD-16) — then
 * how many did not make it. `null` once there is nothing to say.
 */
export function uploadSummary(items: readonly StagedAttachment[]) {
  const sendable = items.filter((item) => item.status.kind !== 'refused');
  const pending = sendable.filter(isPending).length;
  if (pending > 0) {
    return `Uploading ${String(sendable.length - pending)}/${String(sendable.length)}`;
  }
  const failed = sendable.filter((item) => item.status.kind === 'failed');
  if (failed.length > 0) {
    return `${String(failed.length)} attachment(s) did not upload`;
  }
  return null;
}

async function upload(
  target: UploadTarget,
  item: StagedAttachment,
): Promise<AttachmentStatus> {
  try {
    const response = await attachFileToTask(target.workspaceId, target.taskId, {
      file: item.file,
      replace: item.replace,
    });
    if (response.status === 201) {
      return { kind: 'done', storedName: response.data.name };
    }
    const message = (response.data as { message?: unknown } | undefined)
      ?.message;
    return {
      kind: 'failed',
      reason:
        typeof message === 'string' && message
          ? message
          : `${item.file.name} could not be uploaded.`,
    };
  } catch {
    return {
      kind: 'failed',
      reason: `${item.file.name} could not be uploaded.`,
    };
  }
}
